import { useEffect, useState } from 'react'
import { Download, Trash2, Upload } from 'lucide-react'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal, Field } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { fmtBytes, fmtDate } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { DocumentResponse, ProgramResponse } from '@/shared/api/types'
import {
  confirmDocument, deleteDocument, documentDownloadUrl, listDocuments, reserveDocument,
} from '../api'
import { DOC_TYPE_ORDER, DocTypeBadge, docTypeLabel } from '../vocab'

/**
 * Programme documents.
 *
 * Files go through the storage feature's port — reserve a presigned PUT, upload
 * to it directly, then confirm the metadata. This screen never sees an
 * object-store URL: downloads come back as a short-lived presigned link, so a
 * URL copied out of the page stops working rather than becoming a permanent
 * public handle on a receipt with somebody's name on it.
 *
 * Deleting removes the registry row and not the object — the file's lifecycle
 * belongs to storage, and saying so beats leaving a reader to wonder.
 */
export function DocumentsPanel({ program }: { program: ProgramResponse }) {
  const canUpdate = useCan('program:update')
  const [rows, setRows] = useState<DocumentResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const load = () => listDocuments(program.id).then(setRows).catch((e) => setError(explain(e)))

  useEffect(() => {
    let alive = true
    listDocuments(program.id)
      .then((r) => { if (alive) setRows(r) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  const download = async (d: DocumentResponse) => {
    try {
      const { url } = await documentDownloadUrl(program.id, d.id)
      window.open(url, '_blank', 'noopener')
    } catch (e) { setError(explain(e)) }
  }

  const remove = async (d: DocumentResponse) => {
    if (!window.confirm(`Remove "${d.title}" from this programme?`)) return
    try { await deleteDocument(program.id, d.id); load() }
    catch (e) { setError(explain(e)) }
  }

  if (!rows) return <LoadingBlock label="Loading documents…" />

  return (
    <div className="space-y-3 px-1">
      <ErrorStrip message={error} />
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-erp-sm font-medium">{rows.length} document{rows.length === 1 ? '' : 's'}</h4>
        {canUpdate && (
          <button type="button" className="erp-btn h-7 text-erp-xs" onClick={() => setUploading(true)}>
            <Upload className="h-3.5 w-3.5" /> Attach
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-erp-xs text-muted-foreground">Nothing attached.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border px-2 py-1.5">
              <DocTypeBadge code={d.documentTypeCode} />
              <span className="text-erp-sm">{d.title}</span>
              <span className="text-erp-xs text-muted-foreground">
                {fmtBytes(d.sizeBytes)} · {fmtDate(d.createdAt)}
              </span>
              <span className="ml-auto flex gap-1">
                <button type="button" className="erp-btn-ghost h-7 w-7 p-0"
                  aria-label={`Download ${d.title}`} onClick={() => download(d)}>
                  <Download className="h-3.5 w-3.5" />
                </button>
                {canUpdate && (
                  <button type="button" className="erp-btn-ghost h-7 w-7 p-0"
                    aria-label={`Remove ${d.title}`} onClick={() => remove(d)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="text-erp-xs text-muted-foreground">
        Download links expire in fifteen minutes. Removing a document removes it from this
        programme; the stored file follows storage's own retention.
      </p>

      {uploading && (
        <UploadDialog
          programId={program.id}
          onClose={() => setUploading(false)}
          onSaved={() => { setUploading(false); load() }}
        />
      )}
    </div>
  )
}

function UploadDialog({ programId, onClose, onSaved }: {
  programId: string; onClose: () => void; onSaved: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [documentTypeCode, setType] = useState('PROPOSAL')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!file) return
    setBusy(true); setError(null)
    try {
      const reserved = await reserveDocument(
        programId, file.name, file.type || 'application/octet-stream', file.size)
      // The presigned PUT goes straight to the object store — the API server
      // never handles the bytes.
      //
      // The failure is NOT swallowed. Confirming after a failed upload writes a
      // registry row for an object that was never stored: the attach appears to
      // succeed and the download 404s weeks later, when whoever needed the
      // signed agreement is the person who finds out.
      const put = await fetch(reserved.uploadUrl, { method: 'PUT', body: file })
      if (!put.ok) {
        throw new Error(`The file could not be uploaded (${put.status}). Nothing has been attached — try again.`)
      }
      await confirmDocument(programId, {
        fileId: reserved.fileId, title: title || file.name, documentTypeCode,
      })
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Attach a document" footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit} disabled={busy || !file}>
          {busy ? 'Uploading…' : 'Attach'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Note tone="info">
          A receipt or an attendance sheet may carry somebody's name. It is stored behind the
          same access control as the rest of this programme, and its download links expire.
        </Note>
        <Field label="File" required>
          <input type="file" className="erp-input"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFile(f)
              if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''))
            }} />
        </Field>
        <Field label="Title"><input className="erp-input" value={title} maxLength={200}
          onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Type">
          <select className="erp-input" value={documentTypeCode}
            onChange={(e) => setType(e.target.value)}>
            {DOC_TYPE_ORDER.map((t) => <option key={t} value={t}>{docTypeLabel(t)}</option>)}
          </select>
        </Field>
      </div>
    </Modal>
  )
}
